import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FLAT_TYPES = ["1RK", "1BHK", "2BHK", "2.5BHK", "3BHK", "4BHK", "5BHK", "6BHK"];

const emptyForm = { block: "", number: "", floor: "", bhk: "", area_sqft: "", occupancy: "vacant", allotted_parking: false, parking_slot_number: "" };

/** Shared Add/Edit Flat dialog (V2 Flat Master fields) — used by both the Flats list page
 * and the Flat 360 Profile page, so there is exactly one implementation of this create/update
 * flow. Flat ID is always system-generated and is never shown/editable here. */
export default function FlatEditDialog({ open, onOpenChange, editing, onSaved }) {
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        if (!open) return;
        setForm(editing
            ? {
                block: editing.block, number: editing.number, floor: editing.floor || "",
                bhk: editing.bhk || "", area_sqft: editing.area_sqft ?? "",
                occupancy: editing.occupancy || "vacant",
                allotted_parking: !!editing.parking?.allotted,
                parking_slot_number: editing.parking?.slot_number || "",
            }
            : emptyForm);
    }, [open, editing]);

    async function submit(e) {
        e.preventDefault();
        if (!form.bhk) {
            toast.error("Flat Type is required");
            return;
        }
        if (form.allotted_parking && !form.parking_slot_number.trim()) {
            toast.error("Parking Slot Number is required when Allotted Parking is Yes");
            return;
        }
        const payload = {
            block: form.block, number: form.number, floor: form.floor || null,
            bhk: form.bhk || null, area_sqft: form.area_sqft === "" ? null : Number(form.area_sqft),
            occupancy: form.occupancy,
            allotted_parking: form.allotted_parking,
            parking_slot_number: form.allotted_parking ? form.parking_slot_number.trim() : null,
        };
        try {
            if (editing) {
                await api.patch(`/flats/${editing.id}`, payload);
                toast.success("Flat updated");
            } else {
                await api.post("/flats", payload);
                toast.success("Flat added");
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit flat" : "Add flat"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3">Flat Information</p>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2"><Label>Block Number *</Label>
                                    <Input data-testid="flat-block-input" required value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2"><Label>Floor Number *</Label>
                                    <Input data-testid="flat-floor-input" required value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2"><Label>Flat Number *</Label>
                                    <Input data-testid="flat-number-input" required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2"><Label>Flat Type *</Label>
                                    <Select value={form.bhk} onValueChange={(v) => setForm({ ...form, bhk: v })}>
                                        <SelectTrigger data-testid="flat-type-input" className="rounded-sm border-brand-line"><SelectValue placeholder="Select type" /></SelectTrigger>
                                        <SelectContent>
                                            {FLAT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2"><Label>Area (sq. ft.)</Label>
                                    <Input data-testid="flat-area-input" type="number" min="0" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2"><Label>Occupancy *</Label>
                                    <Select value={form.occupancy} onValueChange={(v) => setForm({ ...form, occupancy: v })}>
                                        <SelectTrigger data-testid="flat-occupancy-input" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="vacant">Vacant</SelectItem>
                                            <SelectItem value="owner">Owner Occupied</SelectItem>
                                            <SelectItem value="tenant">Tenant Occupied</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-brand-line">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3">Parking</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Allotted Parking *</Label>
                                <Select value={form.allotted_parking ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, allotted_parking: v === "yes", parking_slot_number: v === "yes" ? form.parking_slot_number : "" })}>
                                    <SelectTrigger data-testid="flat-allotted-parking-input" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="no">No</SelectItem>
                                        <SelectItem value="yes">Yes</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2"><Label>Parking Slot Number {form.allotted_parking && "*"}</Label>
                                <Input data-testid="flat-parking-slot-input" disabled={!form.allotted_parking} required={form.allotted_parking}
                                    value={form.parking_slot_number} onChange={(e) => setForm({ ...form, parking_slot_number: e.target.value })}
                                    placeholder={form.allotted_parking ? "e.g. P-24" : "Not applicable"}
                                    className="rounded-sm border-brand-line disabled:bg-brand-sage/40 disabled:text-brand-inkSoft" />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" data-testid="flat-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">{editing ? "Save" : "Add"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
